import React, { useState, useEffect, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { IconButton, InputAdornment, List, ListItem, ListItemText, Paper, Stack } from '@mui/material';
import { ClearAll } from '@mui/icons-material';

const useStyles = makeStyles(theme => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
  iconRtl: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(2),
  },
  inputRtl: {
    '& label': {
      right: 75,
      left: 'auto',
    },
    '& legend': {
      textAlign: 'right',
      marginRight: 60,
    },
  },
}));

export default function GoogleMapsAutoComplete(props) {
  const { t } = useTranslation();
  const [addressInput, setAddressInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);

  // Sincronizar el estado interno con la prop externa
  useEffect(() => {
    if (props.externalValue && props.externalValue !== addressInput) {
      setAddressInput(props.externalValue);
    } else if (!props.externalValue && addressInput !== '') {
      setAddressInput('');
    }
  }, [props.externalValue]);

  // Limpiar el estado interno cuando no hay valor externo
  useEffect(() => {
    if (!props.value && addressInput !== '') {
      setAddressInput('');
    }
  }, [props.value]);

  const fetchAddressSuggestions = useMemo(() => {
    return async query => {
      if (query.length < 3) {
        setSuggestions([]);
        return;
      }

      let address = query;
      address = address.includes('#') ? address.replace('#', '%23') : address;
      const urlRequest = `https://maps.googleapis.com/maps/api/geocode/json?address=${address}`;
      const additionalOptions = `&components=country:CO&key=AIzaSyCWTd8Bk9enX-j4lzSPWBDRQGim3XLGF2s`;

      try {
        const response = await fetch(urlRequest + additionalOptions);
        const data = await response.json();

        if (data.results && data.results.length > 0) {
          const suggestionsList = data.results
            .slice(0, 5)
            .map((result, index) => ({
              id: index,
              coords: {
                lat: result.geometry.location.lat,
                lng: result.geometry.location.lng,
              },
              addressName: result.formatted_address,
              add: result.formatted_address,
              description: result.formatted_address,
            }));

          setSuggestions(suggestionsList);
        } else {
          setSuggestions([]);
        }
      } catch (error) {
        console.log(error);
        setSuggestions([]);
      }
    };
  }, []);

  // Debounce para las búsquedas
  useEffect(() => {
    if (!isUserTyping || !addressInput) {
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchAddressSuggestions(addressInput);
    }, 500); // Esperar 500ms después de que el usuario deje de escribir

    return () => clearTimeout(timeoutId);
  }, [addressInput, isUserTyping, fetchAddressSuggestions]);

  const handleInputChange = e => {
    const newValue = e.target.value;
    setAddressInput(newValue);
    setIsUserTyping(true);

    // Mostrar sugerencias solo si el usuario está escribiendo y hay texto
    if (newValue.length >= 3) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }

    // Si se borra el input, notificar al padre
    if (newValue === '' && props.onChange) {
      props.onChange(null);
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = suggestion => {
    setAddressInput(suggestion.addressName);
    setShowSuggestions(false);
    setSuggestions([]);
    setIsUserTyping(false);

    // Notificar al componente padre
    if (props.onChange) {
      props.onChange(suggestion);
    }
  };

  const handleInputFocus = () => {
    // Mostrar sugerencias si hay texto y sugerencias disponibles
    if (addressInput.length >= 3 && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    // Ocultar sugerencias después de un pequeño delay para permitir clicks
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const handleKeyDown = e => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <Box position="relative">
      <Box display={'flex'} alignItems={'center'}>
        {props.icon}
        <Typography
          variant="body1"
          color={'GrayText'}
          textTransform={'capitalize'}
        >
          {props.placeholder}
        </Typography>
      </Box>

      <Stack
        direction="row"
      >
        <TextField
          fullWidth
          placeholder={t('pickup_location')}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          value={addressInput}
          autoComplete="off"
        />
        <IconButton
          onClick={() => {
            setAddressInput('');
            setSuggestions([]);
            setShowSuggestions(false);
            if (props.onChange) {
              props.onChange(null);
            }
          }}
        >
          <ClearAll />
        </IconButton>
      </Stack>

      {/* Lista de sugerencias */}
      {showSuggestions && suggestions.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: '200px',
            overflowY: 'auto',
          }}
        >
          <List dense>
            {suggestions.map(suggestion => (
              <ListItem
                key={suggestion.id}
                button
                onClick={() => handleSuggestionClick(suggestion)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemText
                  primary={suggestion.addressName}
                  primaryTypographyProps={{
                    variant: 'body2',
                    noWrap: true,
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}
